import React, { useEffect, useState, useMemo } from "react";
import withRouter from "components/Common/withRouter";
import {
  Container,
  Row,
  Col,
  Spinner,
  Button,
  Input,
  FormFeedback,
  Form,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import classnames from "classnames";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import moment from "moment";
import toast from "react-hot-toast";

import {
  getPayoutById,
  approvePayout,
  rejectPayout,
  markPayoutProcessing,
  markPayoutPaid,
  cancelPayout,
  updateBankDetails,
  getEventDetailedRevenueReport,
  getPayoutsByOrganizer,
} from "api";
import { handleError } from "utils/errorHandler";
import { formatCurrency } from "utils/revenueCalculator";

/* ─── Constants ─────────────────────────────────────── */
const TIMELINE_STEPS = [
  { key: "PENDING",    label: "Request Created" },
  { key: "APPROVED",   label: "Approved" },
  { key: "PROCESSING", label: "Processing" },
  { key: "PAID",       label: "Paid" },
];

const PAYOUT_ACTIONS = [
  {
    type: "approve",
    label: "Approve Payout",
    desc: "Approve this payout request.",
    icon: "mdi mdi-check-circle",
    iconColor: "#2f9e44",
    iconBg: "#d3f9d8",
    show: (s) => s === "PENDING",
  },
  {
    type: "processing",
    label: "Mark as Processing",
    desc: "Mark this payout as processing to initiate the payout.",
    icon: "mdi mdi-progress-clock",
    iconColor: "#1971c2",
    iconBg: "#dbe4ff",
    show: (s) => s === "APPROVED",
  },
  {
    type: "paid",
    label: "Mark as Paid",
    desc: "Confirm this payout has been sent.",
    icon: "mdi mdi-check-decagram",
    iconColor: "#2f9e44",
    iconBg: "#d3f9d8",
    show: (s) => s === "PROCESSING",
  },
  {
    type: "reject",
    label: "Reject Payout",
    desc: "Reject this payout request.",
    icon: "mdi mdi-close-circle",
    iconColor: "#c92a2a",
    iconBg: "#ffe3e3",
    show: (s) => s === "PENDING",
  },
  {
    type: "cancel",
    label: "Cancel Payout",
    desc: "Cancel this payout request.",
    icon: "mdi mdi-cancel",
    iconColor: "#868e96",
    iconBg: "#f1f3f5",
    show: (s) => !["PAID", "REJECTED", "CANCELLED"].includes(s),
  },
];

const BANK_VER_STATUSES = ["PENDING", "VERIFIED", "REJECTED"];

/* ─── Helpers ────────────────────────────────────────── */
const formatDate = (d) =>
  d && moment(d).isValid() ? moment(d).format("D MMM YYYY, hh:mm A") : "—";

const getBankBadgeClass = (s) =>
  s === "VERIFIED" ? "pd-badge-verified" : s === "REJECTED" ? "pd-badge-rejected" : "pd-badge-pending";

const getStatusBadgeClass = (s) => {
  const map = {
    PAID: "pd-badge-paid",
    PROCESSING: "pd-badge-processing",
    REJECTED: "pd-badge-rejected",
    CANCELLED: "pd-badge-cancelled",
    APPROVED: "pd-badge-approved",
  };
  return map[s] || "pd-badge-pending";
};

/* ─── Component ──────────────────────────────────────── */
const PayoutDetailPage = () => {
  document.title = "Payout Details | EventoX";

  const { id } = useParams();
  const navigate = useNavigate();

  const [payout, setPayout]               = useState(null);
  const [loading, setLoading]             = useState(true);
  const [actionModal, setActionModal]     = useState(null); // type string
  const [submitLoading, setSubmitLoading] = useState(false);
  const [adminNotes, setAdminNotes]       = useState("");
  const [currentUserId, setCurrentUserId] = useState("");
  const [editingBankStatus, setEditingBankStatus] = useState(false);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [availBalance, setAvailBalance]   = useState(0);

  useEffect(() => {
    try {
      const auth = localStorage.getItem("authUser");
      if (auth) {
        const parsed = JSON.parse(auth);
        const uid = parsed?.user?.userId ?? parsed?.userId;
        if (uid != null) setCurrentUserId(String(uid));
      }
    } catch (_) {}
  }, []);

  const loadPayout = async () => {
    setLoading(true);
    try {
      const res = await getPayoutById(id);
      const payoutData = res?.data ?? res;
      setPayout(payoutData);
      if (payoutData?.adminNotes) {
        setAdminNotes(payoutData.adminNotes);
      }

      if (payoutData?.eventId) {
        const [reportResp, payoutsResp] = await Promise.all([
          getEventDetailedRevenueReport({ eventId: payoutData.eventId }),
          getPayoutsByOrganizer(payoutData.organizerId)
        ]);

        const reports = reportResp?.data || reportResp || [];
        const report = Array.isArray(reports)
          ? reports.find((r) => String(r.eventId) === String(payoutData.eventId))
          : reports;
        const earnings = Number(report?.sumOrganizerRevenue) || 0;
        setTotalEarnings(earnings);

        const allPayouts = Array.isArray(payoutsResp) ? payoutsResp : payoutsResp?.data || [];
        const otherDeductedSum = allPayouts
          .filter(
            (p) =>
              String(p.eventId) === String(payoutData.eventId) &&
              String(p.id || p.payoutId) !== String(payoutData.id || payoutData.payoutId) &&
              !["CANCELLED", "REJECTED"].includes(String(p.status).toUpperCase())
          )
          .reduce((sum, p) => sum + (Number(p.amount || p.requestedAmount) || 0), 0);

        setAvailBalance(earnings - otherDeductedSum);
      }
    } catch (err) {
      handleError(err);
      toast.error("Failed to load payout details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPayout(); }, [id]);

  /* Derived */
  const statusNormalized = String(payout?.status || "PENDING").toUpperCase();
  const isTerminal       = ["REJECTED", "CANCELLED"].includes(statusNormalized);
  const currentStepIdx   = TIMELINE_STEPS.findIndex((t) => t.key === statusNormalized);
  const org              = payout?.Organizer || payout?.organizer;
  const ev               = payout?.Event || payout?.event;

  const bankDetails = useMemo(() => {
    if (!payout) return null;
    const snap = payout.bankDetails || payout.BankDetail || (payout.bankName ? payout : null);
    if (snap) return snap;
    if (org?.BankDetail) return org.BankDetail;
    if (org?.bankDetails) {
      return Array.isArray(org.bankDetails)
        ? org.bankDetails.find((b) => b.isDefault) || org.bankDetails[0] || null
        : org.bankDetails;
    }
    return null;
  }, [payout, org]);

  const bankVerStatus = String(bankDetails?.verificationStatus || payout?.bankVerificationStatus || "PENDING").toUpperCase();

  /* Form */
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      rejectionReason:  "",
      payoutSentAmount: payout?.amount ?? payout?.requestedAmount ?? "",
      payoutSentDate:   moment().format("YYYY-MM-DD"),
      referenceNumber:  "",
    },
    validate: (values) => {
      const errors = {};
      if (actionModal === "reject" && !values.rejectionReason)
        errors.rejectionReason = "Rejection reason is required";
      if (actionModal === "paid") {
        if (!values.payoutSentAmount || isNaN(values.payoutSentAmount) || Number(values.payoutSentAmount) <= 0)
          errors.payoutSentAmount = "Valid sent amount is required";
        if (!values.payoutSentDate) errors.payoutSentDate = "Sent date is required";
        if (!values.referenceNumber) errors.referenceNumber = "Reference number is required";
      }
      return errors;
    },
    onSubmit: async (values) => {
      setSubmitLoading(true);
      try {
        if (actionModal === "approve") {
          await approvePayout(id, { adminUserId: Number(currentUserId), adminNotes: adminNotes || undefined });
          toast.success("Payout approved");
        } else if (actionModal === "reject") {
          await rejectPayout(id, { adminUserId: Number(currentUserId), rejectionReason: values.rejectionReason });
          toast.success("Payout rejected");
        } else if (actionModal === "processing") {
          await markPayoutProcessing(id, { adminUserId: Number(currentUserId) });
          toast.success("Marked as processing");
        } else if (actionModal === "paid") {
          await markPayoutPaid(id, {
            adminUserId: Number(currentUserId),
            payoutSentAmount: Number(values.payoutSentAmount),
            payoutSentDate: new Date(values.payoutSentDate).toISOString(),
            referenceNumber: values.referenceNumber,
          });
          toast.success("Payout marked as paid");
        } else if (actionModal === "cancel") {
          await cancelPayout(id, { organizerId: Number(payout?.organizerId || org?.userId || 0) });
          toast.success("Payout cancelled");
        }
        setActionModal(null);
        validation.resetForm();
        await loadPayout();
      } catch (err) {
        handleError(err);
        toast.error("Action failed");
      } finally {
        setSubmitLoading(false);
      }
    },
  });

  const handleVerifyBank = async (newStatus) => {
    const bankId = bankDetails?.bankDetailsId || bankDetails?.id;
    if (!bankId) { toast.error("No bank details record found"); return; }
    try {
      await updateBankDetails(bankId, { verificationStatus: newStatus });
      toast.success(`Bank status updated to ${newStatus}`);
      setEditingBankStatus(false);
      await loadPayout();
    } catch (err) {
      handleError(err);
      toast.error("Failed to update bank status");
    }
  };

  const handleDownload = () => {
    toast("Download feature coming soon", { icon: "📥" });
  };

  const nestedBank = bankDetails?.bankDetails || bankDetails;

  const infoRows = payout ? [
    [
      { label: "Organizer",     value: org?.name || payout.organizerId || "—" },
      { label: "Requested On",  value: formatDate(payout.createdAt) },
    ],
    [
      { label: "Event",         value: ev?.title || payout.eventId || "—" },
      { label: "Last Updated",  value: formatDate(payout.updatedAt) },
    ],
    [
      { label: "Country",       value: payout.country || org?.country || "—" },
      {
        label: "Requested By",
        value: org?.name || "—",
        subValue: org?.email || "",
      },
    ],
    [
      { label: "Currency",      value: payout.currency || "—" },
      payout.processedByUser ? {
        label: "Processed By",
        value: payout.processedByUser.name || "—",
        subValue: payout.processedByUser.email || "",
      } : null,
    ],
    [
      { label: "Request Notes", value: payout.requestNotes || payout.notes || "—", span: true },
    ],
    ...(payout.rejectionReason ? [[
      { label: "Rejection Reason", value: payout.rejectionReason, span: true }
    ]] : [])
  ] : [];

  const bankRows = bankDetails ? [
    [
      { label: "Account Holder", value: bankDetails.accountHolderName || "—" },
      { label: "SWIFT Code",     value: nestedBank?.swiftCode || "—" },
    ],
    [
      { label: "Bank Name",      value: bankDetails.bankName || "—" },
      { label: "Currency",       value: bankDetails.currency || payout?.currency || "—" },
    ],
    [
      { label: "Account Number", value: nestedBank?.accountNumber || "—" },
      { label: "Default Account", value: bankDetails.isDefault ? "Yes" : "No" },
    ],
  ] : [];

  const amount        = payout?.amount ?? payout?.requestedAmount ?? 0;
  const currency      = payout?.currency || "USD";
  const afterBalance  = availBalance - amount;

  const visibleActions = PAYOUT_ACTIONS.filter((a) => a.show(statusNormalized));

  /* ── Timeline step dates ── */
  const stepDates = {
    PENDING:    payout?.createdAt || payout?.requestedDate,
    APPROVED:   payout?.processedDate || payout?.updatedAt,
    PROCESSING: payout?.processedDate || payout?.updatedAt,
    PAID:       payout?.payoutSentDate || payout?.processedDate || payout?.updatedAt,
  };

  /* ── Render ── */
  if (loading) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (!payout) {
    return (
      <div className="page-content text-center py-5 text-muted">
        <i className="mdi mdi-alert-circle-outline font-size-36" />
        <p className="mt-2">Payout not found.</p>
        <Button color="primary" outline size="sm" onClick={() => navigate(-1)}>Back</Button>
      </div>
    );
  }

  const actionMeta = PAYOUT_ACTIONS.find((a) => a.type === actionModal);

  return (
    <div className="page-content pd-page">
      {/* ── Styles ── */}
      <style>{`
        .pd-page { background: #f8f9fc; min-height: 100vh; }

        /* Breadcrumb bar */
        .pd-breadcrumb {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: #6c757d; margin-bottom: 18px;
        }
        .pd-breadcrumb a { color: #6c757d; text-decoration: none; }
        .pd-breadcrumb a:hover { color: #3b5bdb; }
        .pd-breadcrumb .sep { color: #adb5bd; }
        .pd-breadcrumb .current { color: #212529; font-weight: 500; }
        .pd-back-btn {
          background: none; border: none; cursor: pointer;
          color: #6c757d; padding: 0; margin-right: 4px; font-size: 15px;
          display: flex; align-items: center; gap: 4px;
          transition: color 0.15s;
        }
        .pd-back-btn:hover { color: #3b5bdb; }

        /* Hero / summary bar */
        .pd-hero {
          background: #fff; border: 1px solid #e9ecef; border-radius: 10px;
          padding: 20px 24px; margin-bottom: 20px;
          display: flex; align-items: center; gap: 32px; flex-wrap: wrap;
        }
        .pd-hero-id { flex-shrink: 0; }
        .pd-hero-id h2 { font-size: 22px; font-weight: 700; color: #212529; margin: 0 0 4px; }
        .pd-hero-id p { font-size: 12.5px; color: #6c757d; margin: 0; }
        .pd-hero-divider {
          width: 1px; height: 48px; background: #e9ecef; flex-shrink: 0;
        }
        .pd-hero-stat { flex: 1; min-width: 120px; }
        .pd-hero-stat .stat-label { font-size: 11.5px; color: #6c757d; margin-bottom: 4px; }
        .pd-hero-stat .stat-val   { font-size: 18px; font-weight: 700; color: #212529; }
        .pd-hero-actions { margin-left: auto; flex-shrink: 0; }
        .pd-download-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 9px 18px; border-radius: 8px;
          border: 1px solid #dee2e6; background: #fff;
          font-size: 13.5px; font-weight: 500; color: #495057;
          cursor: pointer; transition: background 0.15s;
          white-space: nowrap;
        }
        .pd-download-btn:hover { background: #f1f3f5; }

        /* Badges */
        .pd-badge {
          display: inline-block; padding: 4px 12px; border-radius: 50px;
          font-size: 12.5px; font-weight: 500; white-space: nowrap;
        }
        .pd-badge-pending    { background: #fff3cd; color: #e67700; }
        .pd-badge-approved   { background: #d0ebff; color: #1971c2; }
        .pd-badge-processing { background: #dbe4ff; color: #3b5bdb; }
        .pd-badge-paid       { background: #d3f9d8; color: #2f9e44; }
        .pd-badge-verified   { background: #d3f9d8; color: #2f9e44; }
        .pd-badge-rejected   { background: #ffe3e3; color: #c92a2a; }
        .pd-badge-cancelled  { background: #f1f3f5; color: #868e96; }

        /* Cards */
        .pd-card {
          background: #fff; border: 1px solid #e9ecef; border-radius: 10px;
          padding: 22px 24px; margin-bottom: 20px;
        }
        .pd-card-header {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 18px;
        }
        .pd-card-title {
          display: flex; align-items: center; gap: 10px;
          font-size: 15px; font-weight: 600; color: #212529;
        }
        .pd-card-title i { font-size: 18px; color: #3b5bdb; }

        /* Info grid */
        .pd-info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0; }
        .pd-info-cell { padding: 10px 0; border-bottom: 1px solid #f1f3f5; }
        .pd-info-cell:nth-last-child(-n+2) { border-bottom: none; }
        .pd-info-label { font-size: 12px; color: #6c757d; margin-bottom: 3px; }
        .pd-info-val   { font-size: 13.5px; color: #212529; font-weight: 500; }
        .pd-info-sub   { font-size: 12px; color: #6c757d; margin-top: 1px; }
        .pd-info-span  { grid-column: 1 / -1; }

        /* Bank action btn */
        .pd-bank-btn {
          display: flex; align-items: center; gap: 6px;
          padding: 7px 14px; border-radius: 7px;
          border: 1px solid #dee2e6; background: #fff;
          font-size: 13px; font-weight: 500; color: #495057;
          cursor: pointer; transition: background 0.15s;
        }
        .pd-bank-btn:hover { background: #f1f3f5; }

        /* Payment summary */
        .pd-summary-row {
          display: flex; justify-content: space-between;
          align-items: center; padding: 11px 0;
          font-size: 13.5px; border-bottom: 1px solid #f1f3f5;
          color: #495057;
        }
        .pd-summary-row:last-child { border-bottom: none; }
        .pd-summary-row.total-row {
          font-weight: 700; font-size: 14px; color: #212529;
          border-top: 2px solid #e9ecef; margin-top: 4px;
          padding-top: 14px; border-bottom: none;
        }

        /* Sidebar */
        .pd-sidebar { position: sticky; top: 16px; }
        .pd-sidebar-card {
          background: #fff; border: 1px solid #e9ecef; border-radius: 10px;
          padding: 20px; margin-bottom: 16px;
        }
        .pd-sidebar-title {
          font-size: 14px; font-weight: 700; color: #212529; margin-bottom: 16px;
        }

        /* Timeline */
        .pd-timeline { list-style: none; padding: 0; margin: 0; }
        .pd-tl-item {
          display: flex; gap: 14px; position: relative;
          padding-bottom: 18px;
        }
        .pd-tl-item:last-child { padding-bottom: 0; }
        .pd-tl-left { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
        .pd-tl-icon {
          width: 28px; height: 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; flex-shrink: 0; z-index: 1;
        }
        .pd-tl-icon.done  { background: #2f9e44; color: #fff; }
        .pd-tl-icon.curr  { background: #fff; border: 2px solid #3b5bdb; color: #3b5bdb; }
        .pd-tl-icon.future{ background: #e9ecef; color: #adb5bd; }
        .pd-tl-icon.neg   { background: #c92a2a; color: #fff; }
        .pd-tl-line {
          width: 2px; flex: 1; background: #e9ecef; margin-top: 2px;
          min-height: 20px;
        }
        .pd-tl-line.done-line { background: #2f9e44; }
        .pd-tl-body { padding-top: 4px; }
        .pd-tl-label { font-size: 13.5px; font-weight: 600; color: #212529; }
        .pd-tl-label.future { color: #adb5bd; font-weight: 400; }
        .pd-tl-date  { font-size: 12px; color: #6c757d; margin-top: 2px; }

        /* Admin notes */
        .pd-notes-label { font-size: 12px; color: #6c757d; margin-bottom: 8px; }
        .pd-notes-textarea {
          width: 100%; border: 1px solid #dee2e6; border-radius: 7px;
          padding: 10px 12px; font-size: 13px; color: #495057;
          resize: vertical; min-height: 90px; outline: none;
          font-family: inherit;
        }
        .pd-notes-textarea:focus { border-color: #3b5bdb; box-shadow: 0 0 0 2px rgba(59,91,219,0.1); }

        /* Admin action rows */
        .pd-action-row {
          display: flex; align-items: center; gap: 12px;
          padding: 12px 0; border-bottom: 1px solid #f1f3f5;
          cursor: pointer; transition: background 0.1s; border-radius: 7px;
          padding-left: 4px; padding-right: 4px;
          text-decoration: none;
        }
        .pd-action-row:last-child { border-bottom: none; }
        .pd-action-row:hover { background: #f8f9fc; }
        .pd-action-icon {
          width: 34px; height: 34px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 17px; flex-shrink: 0;
        }
        .pd-action-body { flex: 1; }
        .pd-action-label { font-size: 13.5px; font-weight: 600; color: #212529; }
        .pd-action-desc  { font-size: 12px; color: #6c757d; margin-top: 1px; }
        .pd-action-chevron { color: #adb5bd; font-size: 17px; }

        /* Modal */
        .pd-modal-body { padding: 20px 24px; }

        /* Bank status inline editor */
        .pd-bank-status-edit {
          display: flex; align-items: center; gap: 8px; margin-top: 10px;
        }
        .pd-bank-status-edit select {
          border: 1px solid #dee2e6; border-radius: 7px;
          height: 34px; font-size: 13px; padding: 0 8px; min-width: 120px;
        }
        .pd-cancel-edit {
          background: none; border: none; color: #c92a2a;
          font-size: 18px; cursor: pointer; padding: 0; line-height: 1;
        }
      `}</style>

      <Container fluid>
        {/* Breadcrumb */}
        <div className="pd-breadcrumb">
          <button className="pd-back-btn" onClick={() => navigate(-1)}>
            <i className="mdi mdi-arrow-left" />
          </button>
          <a onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>Payout Requests</a>
          <span className="sep"><i className="mdi mdi-chevron-right" /></span>
          <span className="current">Payout Details</span>
        </div>

        {/* Hero bar */}
        <div className="pd-hero">
          <div className="pd-hero-id">
            <div className="d-flex align-items-center gap-2 mb-1">
              <h2 className="mb-0">
                #PAY-{payout.id ?? payout.payoutId}
              </h2>
              <span className={`pd-badge ${getStatusBadgeClass(statusNormalized)}`}>
                {statusNormalized.charAt(0) + statusNormalized.slice(1).toLowerCase()}
              </span>
            </div>
            <p>Requested on {formatDate(payout.createdAt || payout.requestedDate)}</p>
          </div>

          <div className="pd-hero-divider" />

          <div className="pd-hero-stat">
            <div className="stat-label">Requested Amount</div>
            <div className="stat-val">{formatCurrency(amount, currency)} {currency}</div>
          </div>

          <div className="pd-hero-divider" />

          <div className="pd-hero-stat">
            <div className="stat-label">Bank Status</div>
            <div className="mt-1">
              <span className={`pd-badge ${getBankBadgeClass(bankVerStatus)}`}>
                {bankVerStatus.charAt(0) + bankVerStatus.slice(1).toLowerCase()}
              </span>
            </div>
          </div>

          <div className="pd-hero-divider" />

          <div className="pd-hero-stat">
            <div className="stat-label">Payout Status</div>
            <div className="mt-1">
              <span className={`pd-badge ${getStatusBadgeClass(statusNormalized)}`}>
                {statusNormalized.charAt(0) + statusNormalized.slice(1).toLowerCase()}
              </span>
            </div>
          </div>

          <div className="pd-hero-actions">
            <button className="pd-download-btn" onClick={handleDownload}>
              <i className="mdi mdi-download-outline" />
              Download Details
            </button>
          </div>
        </div>

        <Row>
          {/* ── Left column ── */}
          <Col lg={8}>
            {/* Payout Information */}
            <div className="pd-card">
              <div className="pd-card-header">
                <div className="pd-card-title">
                  <i className="mdi mdi-file-document-outline" />
                  Payout Information
                </div>
              </div>
              <div className="pd-info-grid">
                {infoRows.map((row, ri) =>
                  row.map((cell, ci) =>
                    cell ? (
                      <div
                        key={`${ri}-${ci}`}
                        className={`pd-info-cell${cell.span ? " pd-info-span" : ""}`}
                      >
                        <div className="pd-info-label">{cell.label}</div>
                        <div className="pd-info-val">{cell.value}</div>
                        {cell.subValue && (
                          <div className="pd-info-sub">{cell.subValue}</div>
                        )}
                      </div>
                    ) : (
                      <div key={`${ri}-${ci}`} className="pd-info-cell" />
                    )
                  )
                )}
              </div>
            </div>

            {/* Bank Details */}
            <div className="pd-card">
              <div className="pd-card-header">
                <div className="pd-card-title">
                  <i className="mdi mdi-bank-outline" />
                  Bank Details
                  {bankDetails && (
                    <span className={`pd-badge ms-2 ${getBankBadgeClass(bankVerStatus)}`} style={{ fontSize: 12 }}>
                      {bankVerStatus.charAt(0) + bankVerStatus.slice(1).toLowerCase()}
                    </span>
                  )}
                </div>
                {bankDetails && (
                  <div className="d-flex align-items-center gap-2">
                    {!editingBankStatus ? (
                      <button
                        className="pd-bank-btn"
                        onClick={() => setEditingBankStatus(true)}
                      >
                        <i className="mdi mdi-shield-check-outline" />
                        Verify Bank
                      </button>
                    ) : (
                      <div className="pd-bank-status-edit">
                        <select
                          value={bankVerStatus}
                          onChange={(e) => handleVerifyBank(e.target.value)}
                        >
                          {BANK_VER_STATUSES.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <button
                          className="pd-cancel-edit"
                          onClick={() => setEditingBankStatus(false)}
                        >
                          <i className="mdi mdi-close" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {bankDetails ? (
                <div className="pd-info-grid">
                  {bankRows.map((row, ri) =>
                    row.map((cell, ci) => (
                      <div key={`b${ri}-${ci}`} className="pd-info-cell">
                        <div className="pd-info-label">{cell.label}</div>
                        <div className="pd-info-val">{cell.value}</div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <p className="text-muted" style={{ fontSize: 13 }}>
                  No bank details attached to this payout request.
                </p>
              )}
            </div>

            {/* Payment Summary */}
            <div className="pd-card">
              <div className="pd-card-header">
                <div className="pd-card-title" style={{ fontSize: 15 }}>
                  Payment Summary
                </div>
              </div>
              <div>
                <div className="pd-summary-row">
                  <span>Available Balance</span>
                  <span>{formatCurrency(availBalance, currency)}</span>
                </div>
                <div className="pd-summary-row">
                  <span>Total Earnings</span>
                  <span>{formatCurrency(totalEarnings, currency)}</span>
                </div>
                <div className="pd-summary-row">
                  <span>Amount Requested</span>
                  <span>{formatCurrency(amount, currency)}</span>
                </div>
                {(payout.payoutSentAmount) && (
                  <div className="pd-summary-row">
                    <span>Amount Sent</span>
                    <span>{formatCurrency(payout.payoutSentAmount, currency)}</span>
                  </div>
                )}
                {(payout.referenceNumber) && (
                  <div className="pd-summary-row">
                    <span>Reference</span>
                    <span>{payout.referenceNumber}</span>
                  </div>
                )}
                <div className="pd-summary-row total-row">
                  <span>After Payout Balance</span>
                  <span>{formatCurrency(afterBalance, currency)}</span>
                </div>
              </div>
            </div>
          </Col>

          {/* ── Right sidebar ── */}
          <Col lg={4}>
            <div className="pd-sidebar">
              {/* Timeline */}
              <div className="pd-sidebar-card">
                <div className="pd-sidebar-title">Timeline</div>
                <ul className="pd-timeline">
                  {TIMELINE_STEPS.map((step, idx) => {
                    const done    = !isTerminal && idx <= currentStepIdx;
                    const current = !isTerminal && statusNormalized !== "PAID" && idx === currentStepIdx + 1;
                    const future  = !done && !current;
                    const isLast  = idx === TIMELINE_STEPS.length - 1;
                    const stepDate = stepDates[step.key];

                    return (
                      <li key={step.key} className="pd-tl-item">
                        <div className="pd-tl-left">
                          <div className={`pd-tl-icon ${done ? "done" : current ? "curr" : "future"}`}>
                            {done
                              ? <i className="mdi mdi-check" />
                              : current
                              ? <i className="mdi mdi-circle-medium" />
                              : <i className="mdi mdi-circle-outline" style={{ fontSize: 10 }} />
                            }
                          </div>
                          {!isLast && (
                            <div className={`pd-tl-line ${done ? "done-line" : ""}`} />
                          )}
                        </div>
                        <div className="pd-tl-body">
                          <div className={`pd-tl-label ${future && !current ? "future" : ""}`}>
                            {step.label}
                          </div>
                          <div className="pd-tl-date">
                            {done && stepDate ? formatDate(stepDate) : "—"}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {isTerminal && (
                    <li className="pd-tl-item">
                      <div className="pd-tl-left">
                        <div className="pd-tl-icon neg">
                          <i className="mdi mdi-close" />
                        </div>
                      </div>
                      <div className="pd-tl-body">
                        <div className="pd-tl-label" style={{ color: "#c92a2a" }}>
                          {statusNormalized.charAt(0) + statusNormalized.slice(1).toLowerCase()}
                        </div>
                        <div className="pd-tl-date">
                          {formatDate(payout.updatedAt)}
                        </div>
                      </div>
                    </li>
                  )}
                </ul>
              </div>

              {/* Admin Actions & Notes */}
              {visibleActions.length > 0 ? (
                <div className="pd-sidebar-card">
                  <div className="pd-sidebar-title">Admin Actions</div>
                  <div className="mb-3">
                    {visibleActions.map((action) => (
                      <div
                        key={action.type}
                        className="pd-action-row"
                        onClick={() => {
                          validation.resetForm();
                          setActionModal(action.type);
                        }}
                      >
                        <div
                          className="pd-action-icon"
                          style={{ background: action.iconBg, color: action.iconColor }}
                        >
                          <i className={action.icon} />
                        </div>
                        <div className="pd-action-body">
                          <div className="pd-action-label">{action.label}</div>
                          <div className="pd-action-desc">{action.desc}</div>
                        </div>
                        <div className="pd-action-chevron">
                          <i className="mdi mdi-chevron-right" />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-top pt-3">
                    <div className="pd-notes-label" style={{ fontWeight: 600, color: "#495057" }}>Admin Notes</div>
                    <textarea
                      className="pd-notes-textarea"
                      placeholder="Add admin notes (optional)..."
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      style={{ minHeight: "80px", fontSize: "12.5px" }}
                    />
                  </div>
                </div>
              ) : (
                payout.adminNotes && (
                  <div className="pd-sidebar-card">
                    <div className="pd-sidebar-title">Admin Notes</div>
                    <p style={{ fontSize: "13px", color: "#495057", whiteSpace: "pre-wrap", margin: 0 }}>
                      {payout.adminNotes}
                    </p>
                  </div>
                )
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* ── Action Modal ── */}
      <Modal isOpen={!!actionModal} toggle={() => setActionModal(null)} centered>
        <ModalHeader toggle={() => setActionModal(null)}>
          {actionMeta?.label || "Confirm Action"}
        </ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <ModalBody className="pd-modal-body">
            {actionModal === "approve" && (
              <p style={{ fontSize: 14 }}>
                Approve payout of{" "}
                <strong style={{ color: "#2f9e44" }}>
                  {formatCurrency(amount, currency)}
                </strong>{" "}
                for{" "}
                <strong>
                  {org?.name || "the organizer"}
                </strong>
                ?
              </p>
            )}

            {actionModal === "reject" && (
              <>
                <p style={{ fontSize: 14 }}>
                  Reject payout of{" "}
                  <strong style={{ color: "#c92a2a" }}>
                    {formatCurrency(amount, currency)}
                  </strong>
                  ?
                </p>
                <div className="mb-3">
                  <Label style={{ fontSize: 13 }}>Rejection Reason *</Label>
                  <Input
                    name="rejectionReason"
                    type="textarea"
                    rows={3}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.rejectionReason}
                    invalid={!!(validation.touched.rejectionReason && validation.errors.rejectionReason)}
                    placeholder="Explain why the payout is being rejected..."
                    style={{ fontSize: 13 }}
                  />
                  <FormFeedback>{validation.errors.rejectionReason}</FormFeedback>
                </div>
              </>
            )}

            {actionModal === "processing" && (
              <p style={{ fontSize: 14 }}>
                Mark this payout as{" "}
                <strong style={{ color: "#3b5bdb" }}>Processing</strong>?
                This indicates the bank transfer has been initiated.
              </p>
            )}

            {actionModal === "paid" && (
              <>
                <div className="mb-3">
                  <Label style={{ fontSize: 13 }}>Sent Amount *</Label>
                  <Input
                    name="payoutSentAmount"
                    type="number"
                    step="0.01"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.payoutSentAmount}
                    invalid={!!(validation.touched.payoutSentAmount && validation.errors.payoutSentAmount)}
                    style={{ fontSize: 13 }}
                  />
                  <FormFeedback>{validation.errors.payoutSentAmount}</FormFeedback>
                </div>
                <div className="mb-3">
                  <Label style={{ fontSize: 13 }}>Sent Date *</Label>
                  <Input
                    name="payoutSentDate"
                    type="date"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.payoutSentDate}
                    invalid={!!(validation.touched.payoutSentDate && validation.errors.payoutSentDate)}
                    style={{ fontSize: 13 }}
                  />
                  <FormFeedback>{validation.errors.payoutSentDate}</FormFeedback>
                </div>
                <div className="mb-1">
                  <Label style={{ fontSize: 13 }}>Reference Number *</Label>
                  <Input
                    name="referenceNumber"
                    type="text"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.referenceNumber}
                    invalid={!!(validation.touched.referenceNumber && validation.errors.referenceNumber)}
                    placeholder="e.g. TXN-12345678"
                    style={{ fontSize: 13 }}
                  />
                  <FormFeedback>{validation.errors.referenceNumber}</FormFeedback>
                </div>
              </>
            )}

            {actionModal === "cancel" && (
              <>
                <p style={{ fontSize: 14, color: "#c92a2a" }}>
                  Cancel payout of{" "}
                  <strong>{formatCurrency(amount, currency)}</strong>?
                </p>
                <p style={{ fontSize: 13, color: "#6c757d" }}>
                  This action cannot be undone.
                </p>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" outline onClick={() => setActionModal(null)} disabled={submitLoading}>
              Cancel
            </Button>
            <Button
              color={
                actionModal === "approve" ? "info" :
                actionModal === "reject" || actionModal === "cancel" ? "danger" :
                actionModal === "paid" ? "success" : "primary"
              }
              type="submit"
              disabled={submitLoading}
            >
              {submitLoading
                ? <><Spinner size="sm" className="me-1" />Processing...</>
                : "Confirm"
              }
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default withRouter(PayoutDetailPage);